import type {
  ConversationId,
  ConversationThreadId,
  DraftMessageId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  DraftResponse,
  SaveDraftInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { catchApiErrors } from "../db/error-handling";

/**
 * Query keys for draft operations
 */
export const draftKeys = {
  all: ["drafts"] as const,
  lists: () => [...draftKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...draftKeys.lists(), filters] as const,
  details: () => [...draftKeys.all, "detail"] as const,
  detail: (threadId: ConversationThreadId, inReplyToMessageId?: MessageId) =>
    [...draftKeys.details(), threadId, inReplyToMessageId] as const,
};

interface UseDraftOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
  enabled?: boolean;
  initialData?: DraftResponse | null;
}

/**
 * Hook to fetch the current draft for a conversation thread context
 */
export function useDraft({
  conversationId,
  threadId,
  inReplyToMessageId,
  enabled = true,
  initialData,
}: UseDraftOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const queryKey = draftKeys.detail(threadId, inReplyToMessageId);
  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const client = effectApi.getClient();
      const draft = await effectApi.runPromise(
        catchApiErrors(
          client.conversation.getDraft({
            path: { id: conversationId },
            urlParams: { threadId, inReplyToMessageId },
          }),
        ),
      );
      return draft;
    },
    enabled,
    initialData: initialData ?? undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return result;
}

interface UseDraftMutationsOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
}

/**
 * Hook to manage draft mutations with optimistic updates
 */
export function useDraftMutations({
  conversationId,
  threadId,
  inReplyToMessageId,
}: UseDraftMutationsOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  const invalidateDraft = (
    threadId: ConversationThreadId,
    inReplyToMessageId?: MessageId,
  ) => {
    void queryClient.invalidateQueries({
      queryKey: draftKeys.detail(threadId, inReplyToMessageId),
    });
  };

  const saveDraft = useMutation({
    mutationFn: async (input: SaveDraftInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.saveDraft({
            path: { id: conversationId },
            payload: input,
          }),
        ),
      );
    },
    onMutate: async (newDraft) => {
      const queryKey = draftKeys.detail(
        newDraft.threadId,
        newDraft.inReplyToMessageId,
      );

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousDraft = queryClient.getQueryData<DraftResponse | null>(
        queryKey,
      );

      // Optimistically update with new draft
      queryClient.setQueryData<DraftResponse | null>(queryKey, (old) => {
        if (!old) {
          // If no draft exists yet, we can't create a complete optimistic update
          // because we don't have the ID. Return null and let the server response update it.
          return null;
        }

        // Update existing draft - keep existing timestamps for optimistic update
        return {
          ...old,
          content: newDraft.content,
          attachments: newDraft.attachments,
        };
      });

      return { previousDraft, queryKey };
    },
    onSuccess: (data, variables) => {
      // Update with server response
      const queryKey = draftKeys.detail(
        variables.threadId,
        variables.inReplyToMessageId,
      );
      queryClient.setQueryData(queryKey, data);
    },
    onError: (_err, _newDraft, context) => {
      // Rollback on error (error toast already shown in mutationFn)
      if (context?.previousDraft !== undefined && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousDraft);
      }
    },
    onSettled: (_data, _error, variables) => {
      // Refresh from server
      invalidateDraft(variables.threadId, variables.inReplyToMessageId);
      void queryClient.invalidateQueries({ queryKey: draftKeys.lists() });
    },
  });

  const deleteQueryKey = draftKeys.detail(threadId, inReplyToMessageId);

  const deleteDraft = useMutation({
    mutationFn: async (draftId: DraftMessageId) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.conversation.deleteDraft({
            path: { id: conversationId, draftId },
          }),
        ),
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: deleteQueryKey });

      const previousDraft = queryClient.getQueryData<DraftResponse | null>(
        deleteQueryKey,
      );

      queryClient.setQueryData<DraftResponse | null>(deleteQueryKey, null);

      return { previousDraft };
    },
    onSuccess: () => {
      toast("Draft deleted", { variant: "success" });
    },
    onError: (_err, _draftId, context) => {
      if (context?.previousDraft !== undefined) {
        queryClient.setQueryData(deleteQueryKey, context.previousDraft);
      }
    },
    onSettled: () => {
      invalidateDraft(threadId, inReplyToMessageId);
      void queryClient.invalidateQueries({ queryKey: draftKeys.lists() });
    },
  });

  return {
    saveDraft: saveDraft.mutateAsync,
    isSavingDraft: saveDraft.isPending,
    deleteDraft: deleteDraft.mutateAsync,
    isDeletingDraft: deleteDraft.isPending,
    invalidateDraft,
  };
}
